import { useState, useRef, useEffect } from 'react';
import type { CluePublic } from '@cloudfruit-hunts/shared';

interface MagnifierClueProps {
  clue: CluePublic;
}

export function MagnifierClue({ clue }: MagnifierClueProps) {
  const imageUrl = clue.assetUrls[0];
  const config = clue.interactivityConfig.magnifier;

  const containerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isRevealed, setIsRevealed] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const magnifierSize = 100;

  useEffect(() => {
    if (!config || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const { revealZone } = config;

    // Scale reveal zone to actual container size
    const scaleX = rect.width / 800; // Assume 800px original width
    const scaleY = rect.height / 600; // Assume 600px original height

    const zoneX = revealZone.x * scaleX;
    const zoneY = revealZone.y * scaleY;
    const zoneW = revealZone.width * scaleX;
    const zoneH = revealZone.height * scaleY;

    // Check if magnifier center is within reveal zone
    const centerX = position.x + magnifierSize / 2;
    const centerY = position.y + magnifierSize / 2;

    const inZone =
      centerX >= zoneX &&
      centerX <= zoneX + zoneW &&
      centerY >= zoneY &&
      centerY <= zoneY + zoneH;

    setIsRevealed(inZone);
  }, [position, config]);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left - magnifierSize / 2, rect.width - magnifierSize));
    const y = Math.max(0, Math.min(e.clientY - rect.top - magnifierSize / 2, rect.height - magnifierSize));

    setPosition({ x, y });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    e.preventDefault();

    const touch = e.touches[0];
    if (!touch) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(touch.clientX - rect.left - magnifierSize / 2, rect.width - magnifierSize));
    const y = Math.max(0, Math.min(touch.clientY - rect.top - magnifierSize / 2, rect.height - magnifierSize));

    setPosition({ x, y });
  };

  return (
    <div className="py-4">
      <p className="text-lg text-text-dark leading-relaxed mb-4">
        {clue.contentText}
      </p>

      <div
        ref={containerRef}
        className="relative rounded-lg overflow-hidden border border-gray-200 cursor-move select-none"
        onMouseMove={handleMouseMove}
        onMouseDown={() => setIsDragging(true)}
        onMouseUp={() => setIsDragging(false)}
        onMouseLeave={() => setIsDragging(false)}
        onTouchMove={handleTouchMove}
        style={{ touchAction: 'none' }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt="A folded paper map"
            className="w-full h-auto"
            draggable={false}
          />
        )}

        {/* Hidden text: faint, small, only legible while the glass sits on it */}
        {config && isRevealed && (
          <div
            className="absolute text-sm font-semibold text-primary/80 pointer-events-none tracking-wide"
            style={{
              left: config.revealZone.x,
              top: config.revealZone.y,
            }}
          >
            {config.hiddenText}
          </div>
        )}

        {/* Magnifying glass */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: position.x,
            top: position.y,
            width: magnifierSize,
            height: magnifierSize,
          }}
        >
          <div className="w-full h-full rounded-full border-4 border-primary bg-white bg-opacity-20 shadow-lg flex items-center justify-center">
            <svg className="w-10 h-10 text-primary opacity-50" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
